import { createSelector } from 'reselect';
import { orderBy } from 'lodash';
import { StoreState } from '../store-interfaces';
import { updatableCampaignSelector } from './campaignSelectors';

export const selectAudiences = (state: StoreState) => {
  const detailedAudience = state.audiences.audienceDetails;
  const entities = state.audiences.entities || [];

  if (!detailedAudience) {
    return entities;
  }

  const idx = state.audiences.entities.findIndex(
    ({ id }) => id === detailedAudience.id,
  );

  if (idx === -1) {
    return [...entities, detailedAudience];
  }

  return Object.assign([], entities, {
    [idx]: detailedAudience,
  });
};

export const selectAudienceSorted = createSelector(
  [selectAudiences],
  audiences => orderBy(audiences, ad => new Date(ad.updatedAt), ['desc']),
);

export const selectAudiencesPagination = (state: StoreState) =>
  state.audiences.pagination;

const selectedMessengerAd = (state: StoreState) =>
  state.view.chatbotEditor.selectedMessengerAd;

export const linkableCampaignsSelector = createSelector(
  [updatableCampaignSelector],
  campaigns =>
    campaigns.filter(c =>
      ['Draft', 'Active', 'Paused'].includes(c.displayStatus),
    ),
);

export const associatedCampaignSelector = createSelector(
  [updatableCampaignSelector, selectedMessengerAd],
  (campaigns, ad) => {
    if (!ad || !ad.audienceId || !campaigns) {
      return;
    }
    return campaigns.find(
      c =>
        c.displayStatus !== 'Ended' &&
        !!c.campaignAudiencesAttributes &&
        !!c.campaignAudiencesAttributes.find(
          ca => ca.kind === 'included' && ca.audienceId === ad.audienceId,
        ),
    );
  },
);

export const associatedAudienceSelector = createSelector(
  [selectAudiences, selectedMessengerAd],
  (audiences, ad) => {
    if (!ad || !ad.audienceId || !audiences) {
      return;
    }

    return audiences.find(a => a.id === ad.audienceId);
  },
);

export const selectAudienceByIdOrIds = createSelector(
  [selectAudiences, (_, ids: number | null | number[]) => ids],
  (audiences, idOrIds) => {
    return audiences.filter(({ id }) =>
      Array.isArray(idOrIds) ? idOrIds.includes(id) : id === idOrIds,
    );
  },
);
